import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert, Box, Container } from '@mui/material';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetSalesforceSurcharges } from '~/hooks/apis/reservations/useGetSalesforceSurcharges';

import { isLTLEHotelOfferType } from '~/utils/offer';

import { type OfferBuilderProperty, formatPropertyLocation } from '../Content/PropertyInformation';
import OfferBuilderSidebar from '../OfferBuilderSidebar';

import CMChecklistWorkspace from './CMChecklistWorkspace';

export default function OfferBuilderCMChecklistPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const hasOfferLoadError = !isLoadingOffer && !!offerError && !offer;
  const shouldRenderChecklist = isLoadingOffer || (!!offer && isSupportedOfferType);
  const propertyId = isSupportedOfferType
    ? offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id
    : undefined;

  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
  } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled: isSupportedOfferType,
  });
  const {
    data: salesforceSurcharges = [],
    isLoading: isLoadingSurcharges,
    error: surchargesError,
  } = useGetSalesforceSurcharges(offerId ?? null);

  const hotelName = property?.name || propertyId || 'Property';
  const propertyLocation = formatPropertyLocation(property);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>CM Checklist | {hotelName}</title>
      </Helmet>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr)' } }}>
        <OfferBuilderSidebar
          activeItem="CM Checklist"
          hotelCode={property?.hotel_code}
          hotelName={hotelName}
          location={propertyLocation}
          offerId={offerId}
        />

        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, pb: 14 }}>
          {hasOfferLoadError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Unable to load offer details.
            </Alert>
          )}

          {!isLoadingOffer && offer && !isSupportedOfferType && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Offer Builder is only available for flash offers.
            </Alert>
          )}

          {shouldRenderChecklist && (
            <CMChecklistWorkspace
              bookByDate={offer?.book_by_date}
              campaignType={offer?.type}
              channelManager={property?.channel_manager}
              hotelName={hotelName}
              isLoadingProperty={isLoadingOffer || isLoadingProperty}
              isLoadingSurcharges={isLoadingOffer || isLoadingSurcharges}
              offerId={offerId}
              packages={offer?.packages}
              propertyId={property?.id}
              propertyError={propertyError}
              propertyRoomTypes={property?.room_types}
              surcharges={salesforceSurcharges}
              surchargesError={surchargesError}
              travelToDate={offer?.travel_to_date}
              vendorId={offer?.vendor_account_id}
            />
          )}
        </Box>
      </Box>
    </Container>
  );
}
